/**
 * HammerDisplay: HUD widget showing the Hammer of Threia and charge count.
 *
 * Displays a small hammer icon at a fixed HUD position with a numeric charge
 * counter. The icon is hidden when the player has no charges and shown with
 * a celestial glow effect when charges are available.
 *
 * Positioned at the bottom-left corner of the playable area, offset from the
 * Threia Totem display.
 */

import Phaser from 'phaser';
import { TEX_HAMMER_OF_THREIA, TEX_GLOW } from '../utils/constants.js';
import {
  COLOR_HAMMER_BLUE,
} from '../config/game-config.js';

export default class HammerDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene.
   * @param {number} x - Center X position.
   * @param {number} y - Center Y position.
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** @type {number} Current charges displayed. */
    this._charges = 0;

    /** @type {Phaser.GameObjects.Sprite|null} Hammer icon sprite. */
    this._icon = null;

    /** @type {Phaser.GameObjects.Text|null} Charge count text. */
    this._chargeText = null;

    /** @type {Phaser.GameObjects.Text|null} "HAMMER" label. */
    this._label = null;

    /** @type {Phaser.GameObjects.Graphics|null} Glow halo behind icon. */
    this._glowHalo = null;

    /** @type {Phaser.Tweens.Tween|null} Pulsing glow tween. */
    this._glowTween = null;

    /** @type {Phaser.Tweens.Tween|null} Float tween for the icon. */
    this._floatTween = null;

    // Create display elements (hidden initially)
    this._createDisplay();
  }

  /**
   * Show the hammer display with a collect animation.
   * Called when the player first gets hammer charges (from 0).
   * @param {number} charges
   */
  show(charges) {
    this._charges = charges;

    if (!this._icon || !this.scene) return;

    // Make visible
    this._icon.setVisible(true).setAlpha(0).setScale(0);
    this._label.setVisible(true).setAlpha(0);
    this._chargeText.setVisible(true).setAlpha(0);
    this._chargeText.setText(`${this._charges}`);

    // Pop-in animation
    this.scene.tweens.add({
      targets: this._icon,
      scale: 1.6,
      alpha: 1,
      duration: 350,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this._icon || !this._icon.scene) return;
        this.scene.tweens.add({
          targets: this._icon,
          scale: 1.2,
          duration: 200,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // Label + charge text fade-in
    this.scene.tweens.add({
      targets: [this._label, this._chargeText],
      alpha: 1,
      duration: 400,
      delay: 200,
    });

    // Start glow effect
    this._startGlow();

    // Start gentle float
    this._startFloat();

    // Particle burst on collect
    this._playCollectBurst();
  }

  /**
   * Update the charge count display.
   * @param {number} charges
   */
  updateCharges(charges) {
    const prevCharges = this._charges;
    this._charges = charges;

    if (!this._icon || !this.scene) return;

    if (charges > 0 && prevCharges <= 0) {
      // Gained charges from 0 — show the display
      this.show(charges);
      return;
    }

    if (charges <= 0 && prevCharges > 0) {
      // Lost all charges — hide
      this._hide();
      return;
    }

    // Just update the text
    if (this._chargeText) {
      this._chargeText.setText(`${charges}`);

      // Brief scale pulse on charge change
      if (charges !== prevCharges && this._icon.visible) {
        this.scene.tweens.add({
          targets: this._icon,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 120,
          yoyo: true,
          ease: 'Sine.easeOut',
        });
      }
    }
  }

  /**
   * Set state directly (for save restore). No animation.
   * @param {number} charges
   */
  setState(charges) {
    this._charges = charges;

    if (!this._icon) return;

    if (charges > 0) {
      this._icon.setVisible(true).setAlpha(1).setScale(1.2);
      this._label.setVisible(true).setAlpha(1);
      this._chargeText.setVisible(true).setAlpha(1);
      this._chargeText.setText(`${charges}`);
      this._startGlow();
      this._startFloat();
    } else {
      this._icon.setVisible(false);
      this._label.setVisible(false);
      this._chargeText.setVisible(false);
      this._stopGlow();
      this._stopFloat();
    }
  }

  /**
   * Flash the icon on a successful hammer hit.
   * @param {number} hitCount - Current hit in the combo
   * @param {number} maxHits - Total hits needed
   */
  onHit(hitCount, maxHits) {
    if (!this._icon || !this.scene || !this._icon.visible) return;

    // Scale pulse proportional to progress
    const intensity = 1.2 + (hitCount / maxHits) * 0.6;
    this.scene.tweens.add({
      targets: this._icon,
      scaleX: intensity,
      scaleY: intensity,
      duration: 80,
      yoyo: true,
      ease: 'Sine.easeOut',
    });
  }

  /**
   * Destroy all display elements.
   */
  destroy() {
    this._stopGlow();
    this._stopFloat();

    if (this._icon) {
      this._icon.destroy();
      this._icon = null;
    }
    if (this._label) {
      this._label.destroy();
      this._label = null;
    }
    if (this._chargeText) {
      this._chargeText.destroy();
      this._chargeText = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Create the icon, label, and charge text elements (hidden initially).
   * @private
   */
  _createDisplay() {
    // Hammer icon sprite
    this._icon = this.scene.add.sprite(this.x, this.y, TEX_HAMMER_OF_THREIA)
      .setOrigin(0.5)
      .setDepth(210)
      .setScale(1.2)
      .setVisible(false);

    // Charge count text (right of icon)
    this._chargeText = this.scene.add.text(this.x + 12, this.y - 2, '0', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '9px',
      color: '#88ccff',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0, 0.5)
      .setDepth(210)
      .setVisible(false);

    // Small label under the icon
    this._label = this.scene.add.text(this.x, this.y + 14, 'HAMMER', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '7px',
      color: '#888888',
    })
      .setOrigin(0.5, 0)
      .setDepth(210)
      .setVisible(false);
  }

  /**
   * Hide the display with a fade-out animation.
   * @private
   */
  _hide() {
    if (!this._icon || !this.scene) return;

    this._stopGlow();
    this._stopFloat();

    this.scene.tweens.add({
      targets: [this._icon, this._label, this._chargeText],
      alpha: 0,
      duration: 300,
      ease: 'Sine.easeOut',
      onComplete: () => {
        if (this._icon) this._icon.setVisible(false);
        if (this._label) this._label.setVisible(false);
        if (this._chargeText) this._chargeText.setVisible(false);
      },
    });
  }

  /**
   * Start the pulsing glow halo behind the icon.
   * @private
   */
  _startGlow() {
    if (this._glowHalo || !this.scene) return;

    this._glowHalo = this.scene.add.graphics();
    this._glowHalo.setDepth(209);

    const alphaObj = { val: 0.22 };
    this._drawGlowHalo(alphaObj.val);

    this._glowTween = this.scene.tweens.add({
      targets: alphaObj,
      val: 0.06,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._drawGlowHalo(alphaObj.val);
      },
    });
  }

  /**
   * Stop the glow effect.
   * @private
   */
  _stopGlow() {
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glowHalo) {
      this._glowHalo.destroy();
      this._glowHalo = null;
    }
  }

  /**
   * Start a gentle float animation on the icon.
   * @private
   */
  _startFloat() {
    if (this._floatTween || !this._icon || !this.scene) return;

    this._floatTween = this.scene.tweens.add({
      targets: this._icon,
      y: this.y - 2,
      duration: 1600,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Stop the float animation.
   * @private
   */
  _stopFloat() {
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    if (this._icon) {
      this._icon.y = this.y;
    }
  }

  /**
   * Draw the glow halo behind the icon (celestial blue).
   * @private
   * @param {number} alpha
   */
  _drawGlowHalo(alpha) {
    if (!this._glowHalo || !this._icon) return;
    this._glowHalo.clear();

    // Outer soft glow (celestial blue)
    this._glowHalo.fillStyle(COLOR_HAMMER_BLUE, alpha * 0.4);
    this._glowHalo.fillCircle(this._icon.x, this._icon.y, 16);

    // Inner brighter core
    this._glowHalo.fillStyle(COLOR_HAMMER_BLUE, alpha);
    this._glowHalo.fillCircle(this._icon.x, this._icon.y, 10);
  }

  /**
   * Play a particle burst effect.
   * @private
   */
  _playCollectBurst() {
    if (!this.scene || !this._icon) return;

    try {
      const emitter = this.scene.add.particles(this._icon.x, this._icon.y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 10, max: 35 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.9, end: 0 },
        frequency: -1,
        quantity: 8,
      });
      emitter.setDepth(220);
      emitter.explode(8);

      this.scene.time.delayedCall(700, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_) { /* fallback */ }
  }
}
